import { useEffect, useRef } from "react";
import gsap from "gsap";
import type { ToolId } from "./constants";

interface ToolPanelProps {
  activeTool: ToolId;
  onTool: (t: ToolId) => void;
  brushSize: number;
  onBrushSize: (v: number) => void;
  onUndo: () => void;
}

const TOOLS: { id: ToolId; label: string; icon: string; hotkey: string }[] = [
  { id: "shape", label: "Push In", icon: "ri-focus-3-line", hotkey: "1" },
  { id: "pull", label: "Pull Out", icon: "ri-contract-up-down-line", hotkey: "2" },
  { id: "smooth", label: "Smooth", icon: "ri-water-flash-line", hotkey: "3" },
  { id: "reset", label: "Reset", icon: "ri-refresh-line", hotkey: "4" },
];

export function ToolPanel({ activeTool, onTool, brushSize, onBrushSize, onUndo }: ToolPanelProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!rootRef.current) return;
    gsap.fromTo(
      rootRef.current,
      { x: -24, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.9, ease: "power3.out", delay: 0.4 },
    );
  }, []);

  return (
    <div
      ref={rootRef}
      className="pointer-events-auto fixed top-1/2 left-4 md:left-8 -translate-y-1/2 z-30"
    >
      <div
        className="border border-background-700 bg-background-950/85 px-2 py-3 w-[168px] md:w-[188px]"
        style={{ borderColor: "#3A3532" }}
      >
        <div className="px-2 pb-3 mb-2 border-b border-background-800">
          <p className="font-mono-caps text-[10px] text-foreground-400">Tools</p>
        </div>
        <div className="space-y-1">
          {TOOLS.map((t) => {
            const active = t.id === activeTool;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onTool(t.id)}
                aria-pressed={active}
                aria-label={`${t.label} tool (hotkey ${t.hotkey})`}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 whitespace-nowrap cursor-pointer transition-colors border-l-2 ${
                  active
                    ? "bg-background-800 border-accent-500 text-foreground-50"
                    : "border-transparent text-foreground-300 hover:bg-background-900 hover:text-foreground-100"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${t.icon}`} style={{ fontSize: "14px" }} />
                </span>
                <span className="font-mono-caps text-[11px] flex-1">{t.label}</span>
                <span className="font-mono-caps text-[9px] text-foreground-500">{t.hotkey}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-4 pt-3 border-t border-background-800 px-2">
          <div className="flex items-baseline justify-between mb-2">
            <p className="font-mono-caps text-[10px] text-foreground-400">Fingertip Size</p>
            <p className="font-mono-caps text-[10px] text-accent-500 tabular-nums">
              {brushSize.toFixed(1)}
            </p>
          </div>
          <input
            className="pottery-range"
            type="range"
            min={1}
            max={8}
            step={0.1}
            value={brushSize}
            onChange={(e) => onBrushSize(parseFloat(e.target.value))}
            aria-label="Fingertip size"
          />
          <p className="font-mono-caps text-[9px] text-foreground-500 mt-1">Press + / − to adjust</p>
        </div>

        <div className="mt-4 pt-3 border-t border-background-800 px-2">
          <button
            type="button"
            onClick={onUndo}
            className="w-full flex items-center gap-2 justify-center px-3 py-2 border border-background-700 text-foreground-300 hover:text-foreground-50 hover:border-accent-600 font-mono-caps text-[10px] whitespace-nowrap cursor-pointer transition-colors"
            aria-label="Undo last stroke (Cmd or Ctrl + Z)"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-go-back-line" style={{ fontSize: "12px" }} />
            </span>
            Undo
          </button>
        </div>
      </div>
    </div>
  );
}